<template>
	<div class="jup-view">
		<div class="jup-toolbar row row-cols-auto justify-content-between my-3 g-2">
			<div class="col d-flex flex-grow-1 flex-wrap gap-2 mb-2">
				<div class="actions d-flex">
					<button
						v-if="userHasAllNeededJuniperPermissions(['create_permissions'])"
						@click="openCreatePermissionModal()"
						class="j-action-btn jup-action-toolbar-btn--primary"
					>
						<font-awesome-icon :icon="['fas', 'user-lock']" class="me-1" /> Create
					</button>
				</div>
				<div class="actions d-flex">
					<button
						v-if="userHasAllNeededJuniperPermissions(['create_permission_group'])"
						@click="openCreatePermissionGroupModal()"
						class="j-action-btn"
					>
						<font-awesome-icon :icon="['far', 'object-group']" class="me-1" /> Create Group
					</button>
				</div>
				<div class="actions d-flex">
					<button
						v-if="userHasAllNeededJuniperPermissions(['view_permission_group'])"
						@click="openEditPermissionGroupsModal()"
						class="j-action-btn"
					>
						<font-awesome-icon :icon="['fas', 'users-cog']" class="me-1" /> Edit Groups
					</button>
				</div>
			</div>
			<div class="col">
				<select @change="getPermissions" v-model="groupFilter" class="form-select" aria-label="Filter permissions by group">
					<option value="">Filter by Group</option>
					<option v-for="group in groups" :value="group.id" :key="`group${group.id}`">{{ group.name }}</option>
				</select>
			</div>
			<div class="col search">
				<div class="form-group">
					<input type="text" @change="getPermissions" v-model="search" class="form-control" placeholder="Search permissions..." />
				</div>
			</div>
		</div>
		<div class="jup-panel">
			<div class="row" v-if="!loading">
				<div id="tw-permissions-table-wrapper" class="col-md-12">
					<table v-if="permissions.length > 0" class="table table-hover align-middle mb-0 jup-table">
						<thead>
							<tr>
								<th width="50" class="text-center">#</th>
								<th class="text-start">Name</th>
								<th class="text-start" style="min-width: 200px;">Description</th>
								<th>Group</th>
								<th v-if="userHasOneOfNeededJuniperPermissions(['update_permissions', 'delete_permissions'])" width="50">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr class="" v-for="permission in permissions" :key="permission.id + '-key'">
								<td class="text-center">{{ permission.id }}</td>
								<td class="text-start text-nowrap">
									<a role="button" @click="updatePermissionAction = true, openCreatePermissionModal(permission)">{{ permission.name }}</a>
								</td>
								<td>{{ permission.description }}</td>
								<td>
									{{ permission.group.name }}
								</td>
								<td v-if="userHasOneOfNeededJuniperPermissions(['update_permissions', 'delete_permissions'])" class="text-nowrap">
										<button
											class="jup-action-btn me-1"
                                            data-bs-html="true" data-bs-toggle="tooltip" data-bs-placement="top"
                                            v-if="userHasAllNeededJuniperPermissions(['update_permissions'])"
                                            @click="updatePermissionAction = true, openCreatePermissionModal(permission)"
                                            role="button"
                                            title="Edit"
                                        >
                                            <font-awesome-icon :icon="['fas', 'edit']" class="" />
                                        </button>
										<button
											class="jup-action-btn jup-action-btn--danger"
                                            data-bs-html="true" data-bs-toggle="tooltip" data-bs-placement="top"
                                            v-if="userHasAllNeededJuniperPermissions(['delete_permissions'])"
                                            @click="deletePermission(permission)"
                                            role="button"
                                            title="Delete permission"
                                        >
                                            <font-awesome-icon :icon="['fas', 'trash']" class="" />
                                        </button>
								</td>
							</tr>
						</tbody>
					</table>
					<div v-else class="jup-empty">
						There are no permissions data.
					</div>
				</div>
			</div>
			<div v-else class="d-flex justify-content-center p-4">
				<div class="spinner-border" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>
		<!-- Pagination -->
		<div class="mt-2 p-1">
			<Pagination :totalItems="totalPermissions" :currentPage="page" :perPage="perPage" @pageChanged="setPage" :goButton="false" styled="simple" borderActiveColor="" borderTextActiveColor="text-white" :float-right="true" />
		</div>
		<CreatePermissionModal ref="CreatePermissionModal" :update="updatePermissionAction" :permissionForChildComponent="permission" @permissionSaved="handlePermissionSaved" @createPermissionModalClosed="handleCreatePermissionModalClosed" />
		<CreatePermissionGroupModal ref="CreatePermissionGroupModal" @permissionGroupSaved="handlePermissionGroupSaved" />
		<EditPermissionGroupsModal ref="EditPermissionGroupsModal" @editPermissionGroupModalClosed="handleEditPermissionGroupModalClosed" />
	</div>
</template>
<script>
import { mapGetters, mapState } from 'vuex';
import juniperApi from '@/api/juniperApi';
import Pagination from "@/components/juniper/pagination/Pagination";
import CreatePermissionModal from '@/components/juniper/modals/CreatePermissionModal.vue';
import CreatePermissionGroupModal from '@/components/juniper/modals/CreatePermissionGroupModal.vue';
import EditPermissionGroupsModal from '@/components/juniper/modals/EditPermissionGroupsModal.vue';
import juniperUserMixin from "@/mixins/juniperUserMixin";
import { Tooltip } from "bootstrap";
export default {
	name: 'UserPermissions',
	data() {
		return {
			loading: false,
			permissions: [],
			page: 1,
			totalPermissions: 0,
			perPage: 10,
			search: '',
			pagination: null,
            permission: {name: '', group_id: 1, description: ''},
            updatePermissionAction: false,
            groups: [],
            groupFilter: ''
		}
	},
	components: {
		Pagination,
        CreatePermissionModal,
        CreatePermissionGroupModal,
        EditPermissionGroupsModal
	},
	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth'])
	},
    updated() {
		let tooltopElements = document.querySelectorAll('[data-bs-toggle="tooltip"]');

		tooltopElements.forEach(element => {

			let tooltip = Tooltip.getInstance(element);

			if (tooltip) {
				tooltip.dispose();
			}

			if (!Tooltip.getInstance(element)) {
				new Tooltip(element)
			}
		});
	},
	created() {
		this.getPermissions();
        this.setGroups();
	},
	watch: {
        perPage: function () {
            this.getPermissions();
        },
        page: function () {
            this.getPermissions();
        },
    },
	methods: {
		getPermissions() {
			this.loading = true
			juniperApi
				.get(`/permissions?per_page=${this.perPage}&search=${this.search}&page=${this.page}${this.groupFilter ? '&group_id='+this.groupFilter : ''}`)
				.then(({ data }) => {
					this.permissions = data.permissions;
					// this.page = data.page;
					this.totalPermissions = data.count;
					this.loading = false;
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},
		openCreatePermissionModal(permission = this.permission) {
			this.permission = permission;
            this.$refs.CreatePermissionModal.showModal(true);
        },
        openCreatePermissionGroupModal() {
            this.$refs.CreatePermissionGroupModal.showModal(true);
        },
        openEditPermissionGroupsModal() {
            this.$refs.EditPermissionGroupsModal.showModal(true);
        },
		handlePermissionSaved() {
			this.loading = true;
			this.getPermissions();
		},
        handlePermissionGroupSaved() {
            this.getPermissions();
            this.setGroups();
        },
        handleEditPermissionGroupModalClosed(thereWasAChange) {
            if (thereWasAChange) {
                this.getPermissions();
                this.setGroups();
            }
        },
		deletePermission(permission) {
			this.$swal({
                title: "Are you sure?",
                showCancelButton: true,
                icon: "warning",
                html: `Are you sure you want to delete permission <b>${permission.name}</b>?`,
                confirmButtonText: "Yes, delete the permission!",
                showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: "#0FCB4F",
				cancelButtonColor: "#EC2641",
                preConfirm: async () => {
                    try {
                        let res = await juniperApi.delete(`/permissions/${permission.id}`, {});

                        return res;
                    } catch (error) {
                        this.$swal.showValidationMessage(`Request failed: ${error}`);
                    }
                },
                allowOutsideClick: () => !this.$swal.isLoading(),
            }).then(async (result) => {
                if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						
						this.$swal("Deleted!", `Permission deleted successfully!`, "success");
						this.loading = true;
						this.getPermissions();
					} else {
						this.$swal("Request failed!", "There was a problem, permission wasn't deleted.", "error");
					}
                }
            });
		},		
		handleCreatePermissionModalClosed() {
			this.permission = {
                name: '',
                group_id: 1,
                description: ''
			};
			this.updatePermissionAction = false;
		},

		setPage(n) {
            this.page = n;
        },
        async setGroups() {
            const res = await juniperApi.get('/permission-groups');
            this.groups = res.data;
        }
	},
    mixins: [juniperUserMixin]
}
</script>
<style scoped>
#tw-permissions-table-wrapper {
	overflow-x: auto;
}

.jup-panel {
	background: #ffffff;
	border: 1px solid #e2e8f0;
	border-radius: 14px;
	padding: 0;
	min-height: calc(100vh * .72);
	overflow: hidden;
}

.jup-table thead th {
	font-size: 12px;
	color: #64748b;
	font-weight: 700;
	border-bottom-color: #e2e8f0;
	background: #f8fafc;
	padding: 12px 10px;
}

.jup-table tbody td {
	padding: 12px 10px;
	border-bottom-color: #eef2f7;
}

.jup-table tbody tr:hover {
	background: #f8fbff;
}

.jup-table tbody tr:last-child td {
	border-bottom: 0;
}

.jup-table tbody a[role='button'] {
	color: #1d4ed8;
	font-weight: 600;
	text-decoration: none;
}

.jup-table tbody a[role='button']:hover {
	text-decoration: underline;
}

.jup-action-btn {
	width: 30px;
	height: 30px;
	border-radius: 8px;
	border: 1px solid #d6deea;
	background: #f8fafc;
	color: #334155;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	padding: 0;
	transition: all 0.2s ease;
}

.jup-action-btn:hover {
	background: #eef2f7;
	border-color: #b7c4d6;
	color: #1f2937;
}

.jup-action-btn--danger {
	color: #dc2626;
	border-color: #f3c1c1;
	background: #fef2f2;
}

.jup-action-btn--danger:hover {
	background: #fee2e2;
	border-color: #ef9a9a;
	color: #b91c1c;
}

.jup-empty {
	text-align: center;
	padding: 28px 10px;
	font-size: 16px;
	color: #64748b;
}

.jup-action-toolbar-btn--primary {
	background: #2563eb;
	border-color: #2563eb;
	color: #fff;
}

.jup-action-toolbar-btn--primary:hover {
	background: #1d4ed8;
	border-color: #1d4ed8;
	color: #fff;
}
</style>
